import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Ban,
  PlayCircle,
  Send,
  FileText,
  CreditCard,
  Briefcase,
  ClipboardList,
  Shield,
  User,
  AlertTriangle,
  Tag as TagIcon,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";

function statusBadge(status: string) {
  switch (status) {
    case "VERIFIED":
      return <Badge className="bg-green-100 text-green-800 border-green-200">Vérifié</Badge>;
    case "PENDING":
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">En attente</Badge>;
    case "REJECTED":
      return <Badge className="bg-red-100 text-red-800 border-red-200">Rejeté</Badge>;
    case "UNVERIFIED":
      return <Badge variant="outline">Non vérifié</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function docStatusBadge(status: string) {
  switch (status) {
    case "APPROVED":
      return <Badge className="bg-green-100 text-green-800">Approuvé</Badge>;
    case "PENDING":
      return <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>;
    case "REJECTED":
      return <Badge className="bg-red-100 text-red-800">Rejeté</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

export default function ArtisanDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const writable = canWrite(user?.staffRole);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const [missingDocsOpen, setMissingDocsOpen] = useState(false);
  const [missingDocsList, setMissingDocsList] = useState("");
  const [missingDocsMessage, setMissingDocsMessage] = useState("");

  const [tagsOpen, setTagsOpen] = useState(false);
  const [draftTagIds, setDraftTagIds] = useState<string[]>([]);

  const { data, isLoading, isError } = trpc.admin.travauxbtp.getArtisanById.useQuery(
    { id: id! },
    { enabled: !!id, staleTime: 15_000 }
  );

  const utils = trpc.useUtils();

  const suspendMutation = trpc.admin.travauxbtp.suspendArtisan.useMutation({
    onSuccess: () => {
      toast.success("Artisan suspendu");
      setSuspendOpen(false);
      setSuspendReason("");
      utils.admin.travauxbtp.getArtisanById.invalidate({ id: id! });
    },
    onError: (err) => toast.error(err.message),
  });

  const unsuspendMutation = trpc.admin.travauxbtp.unsuspendArtisan.useMutation({
    onSuccess: () => {
      toast.success("Artisan réactivé");
      utils.admin.travauxbtp.getArtisanById.invalidate({ id: id! });
    },
    onError: (err) => toast.error(err.message),
  });

  const requestMissingDocsMutation = trpc.admin.travauxbtp.requestMissingDocs.useMutation({
    onSuccess: () => {
      toast.success("Artisan rebasculé en attente — email envoyé");
      setMissingDocsOpen(false);
      setMissingDocsList("");
      setMissingDocsMessage("");
      utils.admin.travauxbtp.getArtisanById.invalidate({ id: id! });
    },
    onError: (err) => toast.error(err.message),
  });

  const emailMutation = trpc.admin.travauxbtp.sendArtisanEmail.useMutation({
    onSuccess: () => {
      toast.success("Email envoyé");
      setEmailOpen(false);
      setEmailSubject("");
      setEmailBody("");
    },
    onError: (err) => toast.error(err.message),
  });

  const availableTagsQuery = trpc.admin.opsTags.listArtisanTags.useQuery(
    undefined,
    { staleTime: 60_000 }
  );

  const assignTagsMutation = trpc.admin.opsTags.assignArtisanTags.useMutation({
    onSuccess: () => {
      toast.success("Tags mis à jour");
      setTagsOpen(false);
      utils.admin.travauxbtp.getArtisanById.invalidate({ id: id! });
    },
    onError: (err) => toast.error(err.message),
  });

  const openTagsDialog = () => {
    const current = (data as any)?.artisanTags ?? [];
    setDraftTagIds(current.map((t: { id: string }) => t.id));
    setTagsOpen(true);
  };

  const toggleDraftTag = (tagId: string) => {
    setDraftTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((x) => x !== tagId) : [...prev, tagId]
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="py-12 text-center text-sm text-muted-foreground">Chargement...</div>
      </AdminLayout>
    );
  }

  if (isError || !data) {
    return (
      <AdminLayout>
        <div className="py-12 text-center text-sm text-destructive">Artisan introuvable</div>
      </AdminLayout>
    );
  }

  const fullName = [data.User?.firstName, data.User?.lastName].filter(Boolean).join(" ") || data.companyName;
  const isExpired = (date: string | null) => date && new Date(date) < new Date();

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Back link */}
        <Link
          to="/travauxbtp/artisans/all"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à l'annuaire
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary text-xl font-bold">
              {data.companyName?.[0]?.toUpperCase() ?? "A"}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{data.companyName}</h1>
                {statusBadge(data.verificationStatus)}
              </div>
              <p className="text-sm text-muted-foreground">{fullName} · {data.User?.email}</p>
            </div>
          </div>

          {writable && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEmailOpen(true)}>
                <Send className="h-4 w-4 mr-1" />
                Email
              </Button>
              {data.verificationStatus === "VERIFIED" ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMissingDocsOpen(true)}
                  >
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Demander docs manquants
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setSuspendOpen(true)}
                  >
                    <Ban className="h-4 w-4 mr-1" />
                    Suspendre
                  </Button>
                </>
              ) : data.verificationStatus === "REJECTED" ? (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => unsuspendMutation.mutate({ artisanProfileId: id! })}
                  disabled={unsuspendMutation.isPending}
                >
                  <PlayCircle className="h-4 w-4 mr-1" />
                  Réactiver
                </Button>
              ) : null}
            </div>
          )}
        </div>

        {/* Tags VIP / segmentation OPS */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <TagIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Tags :</span>
                {((data as any).artisanTags ?? []).length === 0 ? (
                  <span className="text-sm text-muted-foreground italic">
                    Aucun tag assigné
                  </span>
                ) : (
                  ((data as any).artisanTags as Array<{
                    id: string;
                    name: string;
                    color: string;
                  }>).map((t) => (
                    <span
                      key={t.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border"
                      style={{
                        backgroundColor: `${t.color}20`,
                        borderColor: `${t.color}55`,
                        color: t.color,
                      }}
                    >
                      <TagIcon className="h-3 w-3" />
                      {t.name}
                    </span>
                  ))
                )}
              </div>
              {writable && (
                <Button variant="outline" size="sm" onClick={openTagsDialog}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Modifier tags
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="profil">
          <TabsList>
            <TabsTrigger value="profil">
              <User className="h-4 w-4 mr-1" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="documents">
              <FileText className="h-4 w-4 mr-1" />
              Documents ({data.ArtisanDocuments?.length ?? 0})
            </TabsTrigger>
            <TabsTrigger value="credits">
              <CreditCard className="h-4 w-4 mr-1" />
              Crédits
            </TabsTrigger>
            <TabsTrigger value="projets">
              <Briefcase className="h-4 w-4 mr-1" />
              Projets ({data.recentMatches?.length ?? 0})
            </TabsTrigger>
            <TabsTrigger value="audit">
              <ClipboardList className="h-4 w-4 mr-1" />
              Historique
            </TabsTrigger>
          </TabsList>

          {/* Profil */}
          <TabsContent value="profil">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader><CardTitle className="text-sm">Coordonnées</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {data.User?.email ?? "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {data.User?.phone ?? "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    {data.serviceCity ?? "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    SIRET : {data.siret ?? "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Inscrit le : {data.User?.createdAt
                      ? new Date(data.User.createdAt).toLocaleDateString("fr-FR")
                      : "—"}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-sm">Profil professionnel</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Spécialités :</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(data.artisanSpecialties ?? []).length > 0
                        ? (data.artisanSpecialties as string[]).map((s) => (
                            <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                          ))
                        : <span>—</span>}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Score :</span>{" "}
                    {data.score != null ? `${data.score}/100` : "—"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Statut vérification :</span>{" "}
                    {statusBadge(data.verificationStatus)}
                  </div>
                  {data.certifications && (
                    <div>
                      <span className="text-muted-foreground">Certifications :</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(data.certifications as string[]).map((c) => (
                          <Badge key={c} variant="secondary" className="text-xs">
                            <Shield className="h-3 w-3 mr-0.5" />
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents">
            <Card>
              <CardContent className="pt-4">
                {!data.ArtisanDocuments?.length ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">Aucun document</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Déposé le</TableHead>
                        <TableHead>Expire le</TableHead>
                        <TableHead>Examiné le</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.ArtisanDocuments.map((doc: any) => (
                        <TableRow key={doc.id}>
                          <TableCell className="font-medium">{doc.type}</TableCell>
                          <TableCell>{docStatusBadge(doc.status)}</TableCell>
                          <TableCell className="text-xs">
                            {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString("fr-FR") : "—"}
                          </TableCell>
                          <TableCell className="text-xs">
                            {doc.expiresAt ? (
                              <span className={isExpired(doc.expiresAt) ? "text-destructive font-medium" : ""}>
                                {new Date(doc.expiresAt).toLocaleDateString("fr-FR")}
                                {isExpired(doc.expiresAt) && " (expiré)"}
                              </span>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-xs">
                            {doc.reviewedAt ? new Date(doc.reviewedAt).toLocaleDateString("fr-FR") : "—"}
                          </TableCell>
                          <TableCell>
                            {doc.fileUrl && (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer">
                                  Voir
                                </a>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Credits & Transactions */}
          <TabsContent value="credits">
            <Card>
              <CardContent className="pt-4 space-y-4">
                {data.recentTransactions?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.recentTransactions.map((tx: any) => (
                        <TableRow key={tx.id}>
                          <TableCell className="text-xs">
                            {new Date(tx.createdAt).toLocaleDateString("fr-FR")}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{tx.type}</Badge>
                          </TableCell>
                          <TableCell className={`text-right font-mono ${tx.amount > 0 ? "text-green-600" : "text-destructive"}`}>
                            {tx.amount > 0 ? "+" : ""}{tx.amount}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                            {tx.description ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-8 text-center text-sm text-muted-foreground">Aucune transaction</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projets matchés */}
          <TabsContent value="projets">
            <Card>
              <CardContent className="pt-4">
                {data.recentMatches?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Projet</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Date match</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.recentMatches.map((m: any) => (
                        <TableRow key={m.id}>
                          <TableCell>
                            <Link
                              to={`/particuliers/${m.PrivateProject?.id}`}
                              className="hover:underline font-medium text-sm"
                            >
                              {m.PrivateProject?.title ?? "—"}
                            </Link>
                          </TableCell>
                          <TableCell className="text-xs">{m.PrivateProject?.category ?? "—"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {m.PrivateProject?.status ?? m.status ?? "—"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {m.matchedAt ? new Date(m.matchedAt).toLocaleDateString("fr-FR") : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-8 text-center text-sm text-muted-foreground">Aucun projet matché</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit logs */}
          <TabsContent value="audit">
            <Card>
              <CardContent className="pt-4">
                {data.auditLogs?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Détails</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.auditLogs.map((log: any) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-xs whitespace-nowrap">
                            {new Date(log.createdAt).toLocaleString("fr-FR")}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{log.action}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">
                            {log.details ? JSON.stringify(log.details) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-8 text-center text-sm text-muted-foreground">Aucun historique</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer un email à {data.companyName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Objet</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={5} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Annuler</Button>
            <Button
              disabled={!emailSubject.trim() || !emailBody.trim() || emailMutation.isPending}
              onClick={() => {
                emailMutation.mutate({
                  artisanProfileId: id!,
                  subject: emailSubject.trim(),
                  body: emailBody.trim(),
                });
              }}
            >
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend dialog */}
      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspendre {data.companyName} ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Raison (optionnel)</Label>
            <Textarea value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendOpen(false)}>Annuler</Button>
            <Button
              variant="destructive"
              disabled={suspendMutation.isPending}
              onClick={() => {
                suspendMutation.mutate({
                  artisanProfileId: id!,
                  reason: suspendReason.trim() || undefined,
                });
              }}
            >
              Suspendre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Missing docs dialog — rebascule artisan en attente + email */}
      <Dialog open={missingDocsOpen} onOpenChange={setMissingDocsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demander documents manquants à {data.companyName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              L'artisan repassera en statut « En attente » et recevra un email lui demandant les documents listés.
            </p>
            <div>
              <Label>Documents manquants (un par ligne)</Label>
              <Textarea
                value={missingDocsList}
                onChange={(e) => setMissingDocsList(e.target.value)}
                placeholder={"Kbis\nAttestation décennale\nRIB"}
                rows={4}
              />
            </div>
            <div>
              <Label>Message complémentaire (optionnel)</Label>
              <Textarea
                value={missingDocsMessage}
                onChange={(e) => setMissingDocsMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingDocsOpen(false)}>Annuler</Button>
            <Button
              disabled={requestMissingDocsMutation.isPending}
              onClick={() => {
                const docs = missingDocsList
                  .split("\n")
                  .map((s) => s.trim())
                  .filter(Boolean);
                if (docs.length === 0) {
                  toast.error("Liste de documents requise");
                  return;
                }
                requestMissingDocsMutation.mutate({
                  artisanProfileId: id!,
                  missingDocs: docs,
                  message: missingDocsMessage.trim() || undefined,
                });
              }}
            >
              Envoyer demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tags assignment dialog */}
      <Dialog open={tagsOpen} onOpenChange={setTagsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tags de {data.companyName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {availableTagsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Chargement…</p>
            ) : !availableTagsQuery.data || availableTagsQuery.data.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucun tag artisan créé. Allez dans « Tags OPS » pour en ajouter.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-72 overflow-auto">
                {availableTagsQuery.data.map((t) => {
                  const selected = draftTagIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleDraftTag(t.id)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                        selected ? "" : "opacity-60 hover:opacity-100"
                      }`}
                      style={{
                        backgroundColor: selected ? `${t.color}25` : "transparent",
                        borderColor: t.color,
                        color: t.color,
                      }}
                    >
                      <TagIcon className="h-3 w-3" />
                      {t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagsOpen(false)}>
              Annuler
            </Button>
            <Button
              disabled={assignTagsMutation.isPending}
              onClick={() =>
                assignTagsMutation.mutate({
                  artisanProfileId: id!,
                  tagIds: draftTagIds,
                })
              }
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
